import React, { useState, useEffect } from 'react';
import InsightCharts from './InsightCharts';
import { storage } from '../../utils/storage';
import { useTheme } from '../../hooks/useTheme';

const Insights = () => {
  const [moodData, setMoodData] = useState([]);
  const [pomodoroSessions, setPomodoroSessions] = useState([]);
  const { theme } = useTheme();

  useEffect(() => {
    const moods = storage.get('moodData') || [];
    const sessions = storage.get('pomodoroSessions') || [];
    setMoodData(moods);
    setPomodoroSessions(sessions);
  }, []);

  // Calculate insights
  const averageMood = moodData.length > 0 
    ? (moodData.reduce((acc, m) => acc + m.mood, 0) / moodData.length).toFixed(1)
    : 0;

  const moodByContext = {};
  moodData.forEach(entry => {
    entry.contexts.forEach(context => {
      if (!moodByContext[context]) {
        moodByContext[context] = { total: 0, count: 0 };
      }
      moodByContext[context].total += entry.mood;
      moodByContext[context].count += 1;
    });
  });

  const contextData = Object.entries(moodByContext).map(([context, data]) => ({
    context: context.charAt(0).toUpperCase() + context.slice(1),
    avgMood: (data.total / data.count).toFixed(1)
  }));

  const checkInStreak = moodData.filter(m => {
    const date = new Date(m.date);
    const today = new Date();
    const diffTime = Math.abs(today - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 7;
  }).length;

  const totalFocusHours = Math.floor(pomodoroSessions.reduce((acc, s) => acc + s.duration, 0) / 60);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold mb-2">Insights</h1>
        <p className="text-gray-600">Your mental wellness patterns and progress</p>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className={`p-6 rounded-xl ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
          <h3 className="font-semibold mb-2">Average Mood</h3>
          <div className="text-3xl font-bold text-blue-600">{averageMood}/5</div>
          <p className="text-sm text-gray-500 mt-1">
            {averageMood >= 4 ? 'Doing great!' : averageMood >= 3 ? 'Hanging in there' : 'Room for improvement'}
          </p>
        </div>

        <div className={`p-6 rounded-xl ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
          <h3 className="font-semibold mb-2">Check-in Streak</h3>
          <div className="text-3xl font-bold text-green-600">{checkInStreak}</div>
          <p className="text-sm text-gray-500 mt-1">Last 7 days</p>
        </div>

        <div className={`p-6 rounded-xl ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
          <h3 className="font-semibold mb-2">Focus Time</h3>
          <div className="text-3xl font-bold text-purple-600">{totalFocusHours}h</div>
          <p className="text-sm text-gray-500 mt-1">Total productive time</p>
        </div>
      </div>

      <InsightCharts moodData={moodData} pomodoroSessions={pomodoroSessions} />

      {/* Context Analysis */}
      {contextData.length > 0 && (
        <div className={`p-6 rounded-xl ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
          <h3 className="font-semibold mb-4">Mood by Context</h3>
          <div className="space-y-3">
            {contextData.map(item => (
              <div key={item.context} className="flex items-center justify-between">
                <span className="font-medium">{item.context}</span>
                <div className="flex items-center space-x-2">
                  <div className="w-32 bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full"
                      style={{ width: `${(item.avgMood / 5) * 100}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium">{item.avgMood}/5</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recommendations */}
      <div className={`p-6 rounded-xl bg-gradient-to-r from-blue-50 to-purple-50 ${theme === 'dark' ? 'from-blue-900/20 to-purple-900/20' : ''}`}>
        <h3 className="font-semibold mb-4">Personalized Recommendations</h3>
        <div className="space-y-3">
          {averageMood < 3 && (
            <div className="flex items-start space-x-3">
              <div className="w-2 h-2 bg-blue-600 rounded-full mt-2"></div>
              <p className="text-sm">Your mood has been lower than usual. Consider reaching out to campus counseling or trying our guided meditation exercises.</p>
            </div>
          )}
          {contextData.find(c => c.context === 'Studying' && c.avgMood < 3) && (
            <div className="flex items-start space-x-3">
              <div className="w-2 h-2 bg-blue-600 rounded-full mt-2"></div>
              <p className="text-sm">Studying seems to be affecting your mood. Try shorter study sessions with more breaks.</p>
            </div>
          )}
          {pomodoroSessions.length < 5 && (
            <div className="flex items-start space-x-3">
              <div className="w-2 h-2 bg-blue-600 rounded-full mt-2"></div>
              <p className="text-sm">Increase your focus sessions to improve productivity and mood.</p>
            </div>
          )}
          {averageMood >= 4 && (
            <div className="flex items-start space-x-3">
              <div className="w-2 h-2 bg-green-600 rounded-full mt-2"></div>
              <p className="text-sm">You're doing great! Keep up your current wellness routine.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Insights;